import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Eye, EyeOff, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function RegisterPage() {
    const navigate = useNavigate();
    const { register } = useAuth();
    const [form, setForm] = useState({
        warung_name: "",
        owner_name: "",
        phone: "",
        email: "",
        password: "",
    });
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                email: form.email,
                password: form.password,
                phone: form.phone,
                fullName: form.owner_name,
                role: "mitra",
                shopName: form.warung_name,
            };
            const user = await register(payload);
            toast.success(`Selamat datang Mitra ${user.warung_name}! Bonus Rp 50.000 sudah masuk.`);
            navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-5">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
                <Link to="/" className="flex items-center gap-2.5 mb-6 justify-center">
                    <div className="h-10 w-10 rounded-xl bg-brand-deep dark:bg-brand-green flex items-center justify-center">
                        <span className="font-heading font-bold text-white dark:text-brand-deeper text-lg">W</span>
                    </div>
                    <div className="font-heading font-extrabold text-xl">WARUNGPAY</div>
                </Link>

                <div className="rounded-3xl bg-card border border-border p-7 shadow-xl shadow-brand-deep/5">
                    <div className="flex items-start gap-3 p-3 mb-5 rounded-xl bg-brand-green/10 border border-brand-green/20">
                        <Gift className="h-5 w-5 text-brand-green flex-shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-sm">Bonus Rp 50.000 untuk Mitra Baru!</div>
                            <div className="text-xs text-muted-foreground">Saldo langsung masuk setelah register.</div>
                        </div>
                    </div>

                    <h1 className="font-heading font-bold text-2xl tracking-tight">Daftar Mitra</h1>
                    <p className="text-sm text-muted-foreground mt-1">Gratis, tanpa biaya bulanan.</p>

                    <form onSubmit={submit} className="mt-6 space-y-3.5">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Nama Warung</Label>
                            <Input
                                data-testid="reg-warung-input"
                                placeholder="Warung Sembako Berkah"
                                value={form.warung_name}
                                onChange={upd("warung_name")}
                                required
                                className="h-12 rounded-xl mt-1.5"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Nama Pemilik</Label>
                            <Input
                                data-testid="reg-owner-input"
                                placeholder="Budi Santoso"
                                value={form.owner_name}
                                onChange={upd("owner_name")}
                                required
                                className="h-12 rounded-xl mt-1.5"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">No. HP / WhatsApp</Label>
                            <Input
                                data-testid="reg-phone-input"
                                type="tel"
                                placeholder="08123456789"
                                value={form.phone}
                                onChange={upd("phone")}
                                required
                                className="h-12 rounded-xl mt-1.5"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Email</Label>
                            <Input
                                data-testid="reg-email-input"
                                type="email"
                                placeholder="warung@email.com"
                                value={form.email}
                                onChange={upd("email")}
                                required
                                className="h-12 rounded-xl mt-1.5"
                            />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">Password (min 6)</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    data-testid="reg-password-input"
                                    type={show ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={form.password}
                                    onChange={upd("password")}
                                    required
                                    minLength={6}
                                    className="h-12 rounded-xl pr-12"
                                />
                                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            data-testid="register-submit-btn"
                            disabled={loading}
                            className="w-full bg-brand-green hover:bg-brand-greenDark text-white rounded-xl min-h-[52px] text-base font-semibold shadow-lg shadow-brand-green/25 mt-3"
                        >
                            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Daftar & Klaim Bonus"}
                        </Button>
                    </form>

                    <div className="text-sm text-center mt-5 text-muted-foreground">
                        Sudah jadi mitra?{" "}
                        <Link to="/login" className="font-semibold text-brand-green hover:underline" data-testid="login-link-from-register">
                            Masuk di sini
                        </Link>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
